import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import {
    TFormalOffer,
    TFormalOffers
} from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { Pill, Radio } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../../../../iwIntlProvider/FormattedCurrency';
import { getNonInterestOnlyPaymentAmount } from '../../../../MyOffers.helpers';
import { useOffersPageVersion } from '../../../offersPage.helpers';

type TOfferChoiceButtonsProps = {
    formalOffers: TFormalOffers;
    selectedOfferId?: string;
    drawdownAmount: number;
    updateSelectedOffer: (offer: TFormalOffer) => void;
    interestOnlyPillPeriods: Record<number, number>;
};
type TOfferDurationType = 'shortest' | 'longest' | 'middle';

type TOfferChoiceButtonProps = {
    offer: TFormalOffer;
    isSelected: boolean;
    disabled: boolean;
    drawdownAmount: number;
    onSelect: () => void;
    interestOnlyPillPeriod: number | null;
    durationType: TOfferDurationType;
    showMiddleDescription: boolean;
};

type TOfferPillsProps = {
    durationType: TOfferDurationType;
    interestOnlyPillPeriod: number | null;
    interestOnlyOfferPeriod: number | null;
    hasNoPersonalGuarantee: boolean;
};

export const OfferChoiceButtons = ({
    formalOffers,
    selectedOfferId,
    drawdownAmount,
    updateSelectedOffer,
    interestOnlyPillPeriods
}: TOfferChoiceButtonsProps) => {
    const getDurationType = (
        offer: TFormalOffer,
        formalOffers: TFormalOffers
    ): TOfferDurationType => {
        const durations = formalOffers.map((o) =>
            getDurationInMonths(o.duration!)
        );
        const offerDuration = getDurationInMonths(offer.duration!);

        if (offerDuration === Math.min(...durations)) return 'shortest';
        if (offerDuration === Math.max(...durations)) return 'longest';
        return 'middle';
    };

    const offersSortedByDuration = [...formalOffers].sort(
        (a, b) =>
            getDurationInMonths(a.duration!) - getDurationInMonths(b.duration!)
    );

    return (
        <div>
            <Text className="mt-xl font-med">
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_select_title"
                    defaultMessage="How would you like to repay?"
                />
            </Text>
            <Text className="text-s text-structure-40">
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_select_subHeading"
                    defaultMessage="You can make early repayments with no fees at any time."
                />
            </Text>

            <Radio.Group name="offer">
                {offersSortedByDuration.map((offer) => (
                    <OfferChoiceButton
                        key={offer.offer_id}
                        offer={offer}
                        isSelected={selectedOfferId === offer.offer_id}
                        disabled={drawdownAmount > offer.available_cash!}
                        drawdownAmount={drawdownAmount}
                        onSelect={() => updateSelectedOffer(offer)}
                        interestOnlyPillPeriod={
                            interestOnlyPillPeriods[offer.formal_offer_id] ??
                            null
                        }
                        durationType={getDurationType(offer, formalOffers)}
                        showMiddleDescription={formalOffers.length < 4}
                    />
                ))}
            </Radio.Group>
        </div>
    );
};

const OfferDescription = ({
    durationType
}: {
    durationType: TOfferDurationType;
}): JSX.Element => {
    switch (durationType) {
        case 'shortest':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_shortest"
                    defaultMessage="Repay quickly and minimise interest."
                />
            );
        case 'longest':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_longest"
                    defaultMessage="Keeps your cash flow flexible."
                />
            );
        case 'middle':
            return (
                <FormattedMessage
                    id="_.offer_choice_buttons.offer_description_middle"
                    defaultMessage="Balance your repayments and manage costs."
                />
            );
    }
};

const OfferPills = ({
    durationType,
    interestOnlyPillPeriod,
    interestOnlyOfferPeriod,
    hasNoPersonalGuarantee
}: TOfferPillsProps) => {
    const hasPills =
        durationType === 'shortest' ||
        durationType === 'longest' ||
        interestOnlyPillPeriod !== null ||
        interestOnlyOfferPeriod !== null ||
        hasNoPersonalGuarantee;

    if (!hasPills) return null;

    return (
        <div className="flex flex-wrap gap-s pt-s">
            {durationType === 'shortest' && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.offer_choice_buttons.lowest_total_cost_pill"
                        defaultMessage="Lowest total cost"
                    />
                </Pill>
            )}
            {durationType === 'longest' && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.offer_choice_buttons.lowest_monthly_cost_pill"
                        defaultMessage="Lowest monthly cost"
                    />
                </Pill>
            )}

            {interestOnlyPillPeriod !== null && (
                <Pill>
                    <FormattedMessage
                        id="_.interest_only.interest_only_available"
                        defaultMessage="{interestOnlyPayments} month lower repayment period available"
                        values={{
                            interestOnlyPayments: interestOnlyPillPeriod
                        }}
                    />
                </Pill>
            )}

            {interestOnlyOfferPeriod !== null && (
                <Pill>
                    <FormattedMessage
                        id="_.interest_only.including_interest_only_period"
                        defaultMessage="Including {interestOnlyPeriod} months of only repaying interest"
                        values={{
                            interestOnlyPeriod: interestOnlyOfferPeriod
                        }}
                    />
                </Pill>
            )}

            {hasNoPersonalGuarantee && (
                <Pill variant="green">
                    <FormattedMessage
                        id="_.duration_choice.radio_buttons.no_personal_guarantee_required"
                        defaultMessage="No Personal Guarantee required"
                    />
                </Pill>
            )}
        </div>
    );
};

const OfferChoiceButton = ({
    offer,
    isSelected,
    disabled,
    drawdownAmount,
    onSelect,
    interestOnlyPillPeriod,
    durationType,
    showMiddleDescription
}: TOfferChoiceButtonProps) => {
    const isInterestOnlyOffer = getIsInterestOnlyOffer(offer);
    const interestOnlyPeriod = getFormalOfferInterestOnlyPeriod(offer)!;
    const offersPageVersion = useOffersPageVersion();
    const months = getDurationInMonths(offer.duration!);

    const { data } = useDrawdownRepaymentSchedule({
        offerId: offer.offer_id!,
        drawdownAmount,
        enabled: offersPageVersion === '2.1',
        suspense: true
    });

    const monthlyRepayment = data?.summary?.is_equal_repayment
        ? data.summary.simplified_schedule?.equal_repayments?.amount
        : getNonInterestOnlyPaymentAmount(data?.schedule ?? []);

    return (
        <Radio.Root className="mt-m p-0" variant="frame">
            <Radio.Label className="flex justify-between p-m">
                <div>
                    {offersPageVersion === '2.1' ? (
                        <div className="flex items-center gap-s">
                            <span className="font-bold">
                                <FormattedMessage
                                    id="_.duration_choice.radio_buttons.amount_per_month"
                                    defaultMessage="{amount} per month"
                                    values={{
                                        amount: (
                                            <FormattedCurrency
                                                value={monthlyRepayment!}
                                            />
                                        )
                                    }}
                                />
                            </span>{' '}
                            <span className="font-reg">
                                <FormattedMessage
                                    id="_.duration_choice.radio_buttons.for_up_to_months"
                                    defaultMessage="for up to {months} months"
                                    values={{ months }}
                                />
                            </span>
                        </div>
                    ) : (
                        <Text className="font-med">
                            <FormattedMessage
                                id="_.duration_choice.radio_buttons.monthly_repayments_option"
                                defaultMessage="{months} monthly repayments"
                                values={{ months }}
                            />
                        </Text>
                    )}
                    {(durationType !== 'middle' || showMiddleDescription) && (
                        <Text className="text-s text-structure-40">
                            <OfferDescription durationType={durationType} />
                        </Text>
                    )}

                    <OfferPills
                        durationType={durationType}
                        interestOnlyPillPeriod={interestOnlyPillPeriod}
                        interestOnlyOfferPeriod={
                            isInterestOnlyOffer ? interestOnlyPeriod : null
                        }
                        hasNoPersonalGuarantee={
                            offer.personal_guarantee_proportion === 0
                        }
                    />
                </div>
                <Radio.Input
                    checked={isSelected}
                    disabled={disabled}
                    onChange={onSelect}
                />
            </Radio.Label>
        </Radio.Root>
    );
};
